/* Picker presets — one-click model selection with rationale.
 *
 * Two presets:
 *   - "Pick 4 free"  → top 4 free / freemium models (Groq + OpenRouter)
 *   - "Pick 4 paid"  → top 4 paid frontier models (Anthropic, OpenAI, Google, DeepSeek)
 *
 * Source: GET /api/rankings (served by lopettia-auth). Curated rankings
 * with citations to LMSYS Arena, Artificial Analysis, Aider Polyglot,
 * SWE-bench, and HELM. Living in the auth service means we update once
 * and every product (AI Board, La99, future apps) sees the new list.
 *
 * Each preset button also exposes a "Why these?" popover that shows the
 * methodology + per-model rationale + clickable source links.
 *
 * Fallback: if /api/rankings is unreachable (offline, auth service down),
 * we fall back to a hardcoded shortlist baked into FALLBACK_FREE /
 * FALLBACK_PAID so the button still works.
 */

import { MODELS } from '../data/models';
import { state } from './state';
import { updatePickerMeta, renderSelectedPills } from './selected-pills';
import { renderCostLegend } from './cost-legend';
import { toast } from './toast';
import { t } from './i18n';
import { escapeHtml } from './safe-dom';  // F-17 consolidation

// ---------- Types matching backend/services/model_rankings.py ----------

interface RankingSource {
  name: string;
  url: string;
  kind: string;
}

interface RankingEntry {
  model_id: string;
  provider: string;
  rank: number;
  reason: string;
  sources: RankingSource[];
}

interface Rankings {
  revision: string;
  updated_at: string;
  methodology: string;
  sources_summary: RankingSource[];
  presets: {
    free_top_4: RankingEntry[];
    paid_top_4: RankingEntry[];
  };
}

// ---------- Fallback rankings (if /api/rankings is unreachable) ----------

const FALLBACK_FREE: RankingEntry[] = [
  { model_id: 'llama-3.3-70b-versatile',          provider: 'groq',       rank: 1, reason: 'Top free open-weights model on Groq LPU.', sources: [] },
  { model_id: 'deepseek/deepseek-chat:free',      provider: 'openrouter', rank: 2, reason: 'Frontier-grade general model, free tier.', sources: [] },
  { model_id: 'qwen/qwq-32b:free',                provider: 'openrouter', rank: 3, reason: 'Best free reasoning specialist.',          sources: [] },
  { model_id: 'mixtral-8x7b-32768',               provider: 'groq',       rank: 4, reason: 'MoE family for architectural diversity.',   sources: [] },
];

const FALLBACK_PAID: RankingEntry[] = [
  { model_id: 'claude-opus-5',    provider: 'anthropic', rank: 1, reason: 'Frontier coder + long-context leader (Claude 5).', sources: [] },
  { model_id: 'gpt-4o',           provider: 'openai',    rank: 2, reason: 'Strongest multimodal + ecosystem.',       sources: [] },
  { model_id: 'gemini-2.5-pro',   provider: 'google',    rank: 3, reason: 'Best long-context + price/quality.',      sources: [] },
  { model_id: 'deepseek-reasoner',provider: 'deepseek',  rank: 4, reason: 'Top reasoning per dollar (R1).',          sources: [] },
];

// ---------- Cached fetch ----------

let cachedRankings: Rankings | null = null;
let inflight: Promise<Rankings | null> | null = null;

async function fetchRankings(): Promise<Rankings | null> {
  if (cachedRankings) return cachedRankings;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch('/api/rankings', {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as Rankings;
      cachedRankings = body;
      return body;
    } catch (err) {
      console.warn('Rankings fetch failed, using fallback:', err);
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

// ---------- Apply a preset ----------

function applyPreset(entries: RankingEntry[]): { applied: string[]; missing: string[] } {
  const known = new Set(MODELS.map((m) => m.id));
  const applied: string[] = [];
  const missing: string[] = [];

  entries.forEach((entry) => {
    if (known.has(entry.model_id)) applied.push(entry.model_id);
    else missing.push(entry.model_id);
  });

  if (applied.length === 0) return { applied, missing };

  state.selectedModels.clear();
  applied.forEach((id) => state.selectedModels.add(id));
  localStorage.setItem('lp_aiboard_selected', JSON.stringify([...state.selectedModels]));
  updatePickerMeta();
  renderSelectedPills();
  renderCostLegend();

  applied.forEach((modelId) => {
    window.dispatchEvent(
      new CustomEvent('lp-constellation-toggle', {
        detail: { modelId, wasSelected: false },
      })
    );
  });
  window.dispatchEvent(new CustomEvent('lp-selection-changed'));
  return { applied, missing };
}

async function pickPresetFromSource(kind: 'free' | 'paid'): Promise<void> {
  const r = await fetchRankings();
  const fallback = kind === 'free' ? FALLBACK_FREE : FALLBACK_PAID;
  const entries = r ? (kind === 'free' ? r.presets.free_top_4 : r.presets.paid_top_4) : fallback;
  // Make sure rank order is respected (1..4)
  const ordered = [...entries].sort((a, b) => a.rank - b.rank);
  const { applied, missing } = applyPreset(ordered);

  const i18nKey = kind === 'free' ? 'picker.pickFree.toast' : 'picker.pickPaid.toast';
  const fallbackMsg = kind === 'free'
    ? `Selected top ${applied.length} free models${r ? ` (rev ${r.revision})` : ' (offline list)'}.`
    : `Selected top ${applied.length} paid models${r ? ` (rev ${r.revision})` : ' (offline list)'}.`;
  const msg = (t(i18nKey) || fallbackMsg) + (missing.length ? ` Skipped: ${missing.join(', ')}.` : '');
  toast(msg);
}

// ---------- Why-these modal ----------

const WHY_MODAL_ID = 'picker-why-modal';

// escapeHtml lives in src/modules/safe-dom — F-17 consolidation.

function modelLabel(id: string): string {
  const m = MODELS.find((x) => x.id === id);
  return m ? `${m.label} (${m.provider})` : id;
}

function buildWhyModal(): HTMLElement {
  let m = document.getElementById(WHY_MODAL_ID);
  if (m) return m;
  m = document.createElement('div');
  m.id = WHY_MODAL_ID;
  m.className = 'cloud-modal'; // reuse the modal shell styles
  m.hidden = true;
  m.innerHTML = `
    <div class="cloud-modal-backdrop" data-close="1"></div>
    <div class="cloud-modal-card" role="dialog" aria-modal="true" aria-labelledby="why-modal-title">
      <header class="cloud-modal-head">
        <div>
          <span class="cloud-modal-eyebrow" id="why-modal-eyebrow">WHY THESE 4</span>
          <h2 id="why-modal-title" class="cloud-modal-title">Source &amp; reasoning</h2>
        </div>
        <button class="cloud-modal-close" type="button" aria-label="Close" data-close="1">×</button>
      </header>
      <div class="cloud-modal-body" id="why-modal-body">
        <p class="cloud-modal-empty">Loading…</p>
      </div>
    </div>
  `;
  document.body.appendChild(m);
  m.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.dataset.close === '1') closeWhyModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !m!.hidden) closeWhyModal();
  });
  return m;
}

function closeWhyModal(): void {
  const m = document.getElementById(WHY_MODAL_ID);
  if (m) m.hidden = true;
}

function renderEntries(entries: RankingEntry[]): string {
  const sorted = [...entries].sort((a, b) => a.rank - b.rank);
  return sorted.map((e) => `
    <article class="why-entry">
      <div class="why-entry-head">
        <span class="why-rank">#${e.rank}</span>
        <span class="why-model">${escapeHtml(modelLabel(e.model_id))}</span>
      </div>
      <p class="why-reason">${escapeHtml(e.reason)}</p>
      ${e.sources.length ? `
        <ul class="why-sources">
          ${e.sources.map((s) => `
            <li><a href="${escapeHtml(s.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(s.name)}</a>
              <span class="why-source-kind">— ${escapeHtml(s.kind)}</span></li>
          `).join('')}
        </ul>
      ` : ''}
    </article>
  `).join('');
}

async function openWhyModal(kind: 'free' | 'paid'): Promise<void> {
  buildWhyModal().hidden = false;
  const eyebrow = document.getElementById('why-modal-eyebrow')!;
  const title   = document.getElementById('why-modal-title')!;
  const body    = document.getElementById('why-modal-body')!;
  eyebrow.textContent = kind === 'free' ? 'BEST FREE LLMs · WHY' : 'BEST PAID LLMs · WHY';
  title.textContent   = kind === 'free' ? 'Top 4 free models' : 'Top 4 paid models';
  body.innerHTML = '<p class="cloud-modal-empty">Loading rankings…</p>';

  const r = await fetchRankings();
  if (!r) {
    const entries = kind === 'free' ? FALLBACK_FREE : FALLBACK_PAID;
    body.innerHTML = `
      <p class="cloud-modal-empty">Showing fallback list (auth service unreachable).</p>
      <div class="why-list">${renderEntries(entries)}</div>
    `;
    return;
  }

  const entries = kind === 'free' ? r.presets.free_top_4 : r.presets.paid_top_4;
  body.innerHTML = `
    <p class="why-methodology">${escapeHtml(r.methodology)}</p>
    <div class="why-revision">Rev ${escapeHtml(r.revision)} · updated ${escapeHtml(r.updated_at.slice(0, 10))}</div>
    <div class="why-list">${renderEntries(entries)}</div>
    <details class="why-sources-all">
      <summary>All cited sources</summary>
      <ul class="why-sources">
        ${r.sources_summary.map((s) => `
          <li><a href="${escapeHtml(s.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(s.name)}</a>
            <span class="why-source-kind">— ${escapeHtml(s.kind)}</span></li>
        `).join('')}
      </ul>
    </details>
  `;
}

// ---------- Exports ----------

export async function pickFreePreset(): Promise<void> {
  await pickPresetFromSource('free');
}

export async function pickPaidPreset(): Promise<void> {
  await pickPresetFromSource('paid');
}

export function attachPickerPresetHandlers(): void {
  document.getElementById('pick-free-btn')?.addEventListener('click', (e) => {
    e.preventDefault();
    pickFreePreset();
  });
  document.getElementById('pick-paid-btn')?.addEventListener('click', (e) => {
    e.preventDefault();
    pickPaidPreset();
  });
  document.getElementById('pick-free-why-btn')?.addEventListener('click', (e) => {
    e.preventDefault();
    openWhyModal('free');
  });
  document.getElementById('pick-paid-why-btn')?.addEventListener('click', (e) => {
    e.preventDefault();
    openWhyModal('paid');
  });
  // Prefetch rankings on idle so the first preset click is instant
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => fetchRankings(), { timeout: 3000 });
  } else {
    setTimeout(() => fetchRankings(), 1500);
  }
}

if (typeof window !== 'undefined') {
  (window as any).aiBoardPickFree = pickFreePreset;
  (window as any).aiBoardPickPaid = pickPaidPreset;
}
